<template>
  <generic-modal
    v-bind="$props"
    :value="value"
    :is-disabled="isLoading"
    :is-loading="isLoading"
    :title="deal.name"
    hide-cancel
    hide-louis
    confirm-text="Close"
    width="400"
    @modal-confirm="$emit('input', false)"
    v-on="$listeners"
  >
    <template>
      <div
        v-for="(field, index) in fields"
        :key="field.title"
        :class="{
          'grey lighten-2': index % 2 === 0,
        }"
        class="px-1 d-flex justify-space-between"
        style="max-width: 400px;"
      >
        <span>{{ field.title }}</span>
        <span class="font-weight-bold text-right" style="max-width: 70%">
          <template v-if="Array.isArray(field.value)">
            <div v-for="(value, idx) in field.value" :key="`${value}${idx}`">{{ value || 'NOT SET' }}</div>
          </template>
          <span v-else>{{ field.value || 'NOT SET' }}</span>
        </span>
      </div>
    </template>
  </generic-modal>
</template>

<script>
import GenericModal from '@/components/modals/generic-modal'
import { mapGetters } from 'vuex'
import { formatDate } from '@/utils/date'

export default {
  name: 'DealViewModal',
  components: {
    GenericModal,
  },
  props: {
    deal: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('options', ['formattedOption']),
    fields() {
      if (!this.deal) return []
      const {
        commission_rates,
        conversion_rate_guarantee,
        countries,
        currency,
        end,
        notes,
        partner,
        payment_type,
        offer_types,
        start,
        status,
        tags,
        type,
      } = this.deal
      return [
        { title: 'Status', value: status },
        { title: 'Deal Type', value: this.formattedOption('dealTypes', type) },
        { title: 'Start', value: formatDate(start, { format: 'words' }) },
        { title: 'End', value: (end && formatDate(end, { format: 'words' })) || 'Open-ended' },
        { title: 'Currency', value: currency },
        { title: 'Payment Type', value: payment_type },
        {
          title: 'Countries',
          value: countries && countries.map(code => this.formattedOption('countries', code)).sort(),
        },
        {
          title: 'CRG',
          value:
            conversion_rate_guarantee && Object.keys(conversion_rate_guarantee).length
              ? Object.entries(conversion_rate_guarantee)
                  .map(([code, data]) => [this.formattedOption('countries', code), data])
                  .sort(([aCountry], [bCountry]) => {
                    if (aCountry < bCountry) return -1
                    if (aCountry > bCountry) return 1
                    return 0
                  })
                  .map(
                    ([country, { percentage, min_lead_volume }]) =>
                      `${country}: ${percentage}% AT ${min_lead_volume} leads`
                  )
              : false,
        },
        {
          title: 'Commission Rates',
          value:
            commission_rates && Object.keys(commission_rates).length
              ? Object.entries(commission_rates)
                  .map(([code, data]) => [this.formattedOption('countries', code), data])
                  .sort(([aCountry], [bCountry]) => {
                    if (aCountry < bCountry) return -1
                    if (aCountry > bCountry) return 1
                    return 0
                  })
                  .map(this.calculateCommissionRate(currency))
              : false,
        },
        {
          title: 'Offer Types',
          value: offer_types && offer_types.map(type => this.formattedOption('offerTypes', type)).join(', '),
        },
        { title: 'Notes', value: notes },
        { title: 'Tags', value: tags },
      ]
    },
  },
  methods: {
    getCurrency(val) {
      if (val === 'GBP') return '£'
      if (val === 'EUR') return '€'
      return '$'
    },
    calculateCommissionRate(currency) {
      return ([country, { value, qualifier }]) => {
        const qualifyingStatement = qualifier ? `IF ${qualifier}` : ''
        return [`${country}:`, `${this.getCurrency(currency)}${value}`, qualifyingStatement]
          .filter(Boolean)
          .join(' ')
          .trim()
      }
    },
  },
}
</script>
